/**
 * QuizList component - displays all quizzes for the user
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PiCalendarDotsBold } from 'react-icons/pi';
import './QuizList.css';
import DeleteQuizDialog from './DeleteQuizDialog';
import EditIcon from './EditIcon';
import { QuestionType } from '../db/schema';

interface Quiz {
    id: number;
    title: string;
    description: string;
    userId: number;
    questionTypes?: number[];
}

const questionTypeLabels: Record<QuestionType, string> = {
    [QuestionType.SHORT_ANSWER]: 'Short Answer',
    [QuestionType.MULTIPLE_CHOICE]: 'Multiple Choice',
    [QuestionType.TRUE_FALSE]: 'True/False',
};

export default function QuizList() {
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [quizToDelete, setQuizToDelete] = useState<Quiz | null>(null);
    const [creatingGroupQuiz, setCreatingGroupQuiz] = useState<number | null>(
        null
    );

    const fetchQuizzes = async () => {
        try {
            const response = await fetch('/api/quizzes');
            if (response.ok) {
                const data = await response.json();
                setQuizzes(data.quizzes);
            }
        } catch (error) {
            console.error('Failed to fetch quizzes:', error);
        }
    };

    useEffect(() => {
        fetchQuizzes();
    }, []);

    const handleDeleteClick = (quiz: Quiz) => {
        setQuizToDelete(quiz);
        setIsDeleteModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsDeleteModalOpen(false);
        setQuizToDelete(null);
        fetchQuizzes(); // Refresh quizzes after deletion
    };

    const handleStartGroupQuiz = async (quizId: number) => {
        setCreatingGroupQuiz(quizId);
        try {
            const response = await fetch('/api/group-quiz/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ quizId }),
            });

            if (!response.ok) {
                throw new Error('Failed to create group quiz');
            }

            const data = await response.json();
            navigate(`/group-quiz/${data.hash}/host`);
        } catch (error) {
            console.error('Failed to create group quiz:', error);
            alert('Failed to create group quiz. Please try again.');
        } finally {
            setCreatingGroupQuiz(null);
        }
    };

    return (
        <div className="quizList">
            <h1 className="quizTitle">My Quizzes</h1>
            <div className="quizListHeader">
                <button
                    className="createQuizButton"
                    onClick={() => navigate('/quiz/create')}
                >
                    + Create New Quiz
                </button>
            </div>

            {quizzes.map((quiz) => (
                <div key={quiz.id} className="quizItem">
                    <Link to={`/quiz/${quiz.id}/edit`}>
                        <div className="quizTitleRow">
                            <h3>{quiz.title}</h3>
                            {quiz.questionTypes &&
                                quiz.questionTypes.length > 0 && (
                                    <div className="questionTypeTags">
                                        {quiz.questionTypes.map((type) => (
                                            <span
                                                key={type}
                                                className="questionTypeTag"
                                            >
                                                {
                                                    questionTypeLabels[
                                                        type as QuestionType
                                                    ]
                                                }
                                            </span>
                                        ))}
                                    </div>
                                )}
                        </div>
                    </Link>
                    <p>{quiz.description}</p>

                    <div className="quizItemButtons">
                        <div className="quizItemButtonsLeft">
                            <Link to={`/quiz/${quiz.id}/take`}>
                                <div
                                    className="quizItemButtonsIcons"
                                    title="Start Quiz"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        width="20"
                                        height="20"
                                    >
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                    Take Quiz
                                </div>
                            </Link>
                            <button
                                className="groupQuizButton"
                                title="Start Group Quiz"
                                onClick={() => handleStartGroupQuiz(quiz.id)}
                                disabled={creatingGroupQuiz === quiz.id}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    width="20"
                                    height="20"
                                    strokeWidth="2"
                                >
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {creatingGroupQuiz === quiz.id
                                    ? 'Creating...'
                                    : 'Group Quiz'}
                            </button>
                        </div>
                        <div className="quizItemButtonsRight">
                            <Link to={`/quiz/${quiz.id}/edit`}>
                                <button
                                    className="historyButton"
                                    title="Edit Quiz"
                                    type="button"
                                >
                                    <EditIcon size={20} />
                                </button>
                            </Link>
                            <Link to={`/quiz/${quiz.id}/history`}>
                                <button
                                    className="historyButton"
                                    title="View History"
                                    type="button"
                                >
                                    <PiCalendarDotsBold size={20} />
                                </button>
                            </Link>
                            <button
                                className="deleteButton"
                                title="Delete Quiz"
                                type="button"
                                onClick={() => handleDeleteClick(quiz)}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {quizToDelete && (
                <DeleteQuizDialog
                    isOpen={isDeleteModalOpen}
                    onClose={handleCloseModal}
                    quizId={quizToDelete.id}
                    quizTitle={quizToDelete.title}
                />
            )}
        </div>
    );
}
